import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type {
  DeleteReminderByUserResponse,
  RecurrenceScope,
} from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

interface UseDeleteReminderByUserOptions {
  onSuccess?: (response: DeleteReminderByUserResponse) => void;
  onError?: (error: Error) => void;
}

export const useDeleteReminderByUser = (
  options?: UseDeleteReminderByUserOptions,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      userId,
      scope,
    }: {
      eventId: number;
      userId: number;
      scope?: RecurrenceScope;
    }) => eventsApi.deleteReminderByUser(eventId, userId, scope),
    onSuccess: (response) => {
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      if (response.failed_entry_ids?.length) {
        toast({
          title: t("common.error"),
          description: t("eventManage.reminderDeleteFailed"),
          variant: "error",
        });
      }
      options?.onSuccess?.(response);
    },
    onError: (error: Error) => {
      console.error("Failed to delete reminders for user:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderDeleteFailed"),
        variant: "error",
      });
      options?.onError?.(error);
    },
  });

  const deleteReminderByUser = useCallback(
    async (eventId: number, userId: number, scope?: RecurrenceScope) => {
      return mutation.mutateAsync({ eventId, userId, scope });
    },
    [mutation],
  );

  return {
    deleteReminderByUser,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
